import React, {
  useEffect,
} from 'react';

import * as Yup from 'yup';
import { useFormik } from 'formik';

import { Button } from '@waglabs/button-next';

import type { UseStepperReturnType } from '../../../../../../hooks/useStepper';

import { getFormikFieldHelper } from '../../../../../../utils/getFormikHelper';
import { phoneSchema } from '../../../../../../utils/validation-schemas/phone';

import { PhoneTextField } from '../../../../../../components/PhoneTextField/PhoneTextField';
import { usePostAuthenticationSession } from '../../../../../../hooks/api/usePostAuthenticationSession';

import { Auth } from '../../../../../../constants';

type LoginByPhoneFormStep1Props = UseStepperReturnType & {};

const FormField = Object.freeze({
  Phone: 'phone',
});

const schema = Yup.object().shape({
  [FormField.Phone]: phoneSchema
    .label('Phone'),
});


export const LoginByPhoneFormStep1 = ({
  handleNext,
  setMeta,
}: LoginByPhoneFormStep1Props) => {
  const {
    execute: createAuthenticationSession,
    isSuccess,
    data,
    error: postAuthenticationSessionError,
    errorMessage: postAuthenticationSessionErrorMessage,
    reset,
  } = usePostAuthenticationSession();
  const {
    errors,
    isValid,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    handleBlur,
    setFieldValue,
    setSubmitting,
  } = useFormik({
    isInitialValid: false,
    initialValues: schema.cast(),
    validationSchema: schema,
    onSubmit: (formValues) => {
      setMeta({
        [FormField.Phone]: formValues[FormField.Phone],
      });
      createAuthenticationSession({
        userRoleIdentifier: formValues[FormField.Phone],
        userRoleIdentificationMethod: Auth.AllowedMethods.Phone,
        userRoleType: Auth.RoleType.Owner,
      });
    },
  });

  const formikFieldHelper = getFormikFieldHelper({
    errors,
    touched,
  });

  const fieldError = (
    postAuthenticationSessionError || formikFieldHelper(FormField.Phone).hasError
  );
  const fieldHelperText = (
    postAuthenticationSessionErrorMessage || formikFieldHelper(FormField.Phone).errorMessage
  );

  useEffect(() => {
    // Reset on mount
    reset();
  }, [
    reset,
  ]);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    handleNext();
  }, [
    isSuccess,
    handleNext,
    data,
  ]);

  useEffect(() => {
    if (!postAuthenticationSessionError) {
      return;
    }
    setSubmitting(false);
  }, [
    postAuthenticationSessionError,
    setSubmitting,
  ]);

  return (
    <>
      <form
        onSubmit={handleSubmit}
      >
        <PhoneTextField
          label="Phone number"
          variant="outlined"
          name={FormField.Phone}
          international={false}
          inputProps={{
            fontFamily: 'muli',
          }}
          countrySelectProps={{
            disabled: true,
          }}
          containerProps={{

            border: 'unset',
          }}
          style={{
            // This will be applied to the PhoneInput
            // not on the TextField
            backgroundColor: 'white',
            border: '1px solid',
            borderColor: formikFieldHelper(FormField.Phone)
              .hasError
              ? 'red'
              : '#D1D1D1',
          }}
          error={fieldError}
          helperText={fieldHelperText}
          value={values[FormField.Phone]}
          onChange={(phone) => {
            setFieldValue(
              FormField.Phone,
              phone,
            );
          }}
          onBlur={handleBlur}
          fullWidth
        />
        <Button
          variant="contained"
          type="submit"
          disabled={!isValid || isSubmitting}
          mt="12px"
          mb="24px"
        >
          Continue
        </Button>
      </form>
    </>
  );
};
